import ButtonWithIcon from 'components/Core/Button/ButtonWithIcon';
import React, { Fragment, useCallback, useEffect, useLayoutEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { FieldValues, FormProvider, useForm } from 'react-hook-form';
import { useFetcher } from 'react-router-dom';
import { DevTool } from '@hookform/devtools';
import Dialog, { DialogActions, DialogContent, DialogTitle } from '../../../components/Dialog/Dialog';
import TextFieldControlled from '../../../components/Form/ControlledFields/TextFieldControlled';
import Label from '../../../components/Core/Label/Label';
import ConnectForm from '../../../components/Form/ConnectForm';
import { TOAST_SEVERITY } from '../../../components/Core/Toast/Toast';
import { useSnackbar } from '../../../context/SnackbarContext';
import useDisplayError from '../../../hook/useDisplayError';

export function PriceTypeForm() {
  const { t } = useTranslation(['parameters/priceList/priceTypeList', 'form', 'common']);

  return (
    <ConnectForm>
      {({ register }) => (
        <fieldset>
          <Label>{t('fields.name.label')}</Label>
          <TextFieldControlled placeholder={t('fields.name.placeholder')} {...register('name', { required: true })} />
        </fieldset>
      )}
    </ConnectForm>
  );
}

function NewPriceType() {
  const fetcher = useFetcher();
  const { t } = useTranslation(['parameters/priceList/priceTypeList', 'form', 'common']);
  const [layoutElement, setLayoutElement] = useState<Element | null>(null);
  const snackbar = useSnackbar();
  useDisplayError(fetcher.data);
  const [open, setIsOpen] = useState(false);
  const methods = useForm({ mode: 'onChange', defaultValues: { name: null } });
  const { reset } = methods;

  useLayoutEffect(() => {
    setLayoutElement(document.querySelector('#price-list-layout'));
  }, []);

  const onCancel = useCallback(() => {
    setIsOpen(false);
    reset();
  }, [reset, setIsOpen]);

  const onSubmit = (data: FieldValues) => {
    fetcher.submit({ type: 'new', ...data }, { method: 'post', encType: 'application/json' });
  };

  useEffect(() => {
    if (fetcher.data && fetcher.data.status === 200 && fetcher.data.config.url === 'pricetype/new') {
      snackbar?.setAlert({
        message: t('createSuccessfullyMessage'),
        severity: TOAST_SEVERITY.success,
      });
      onCancel();
    }
  }, [fetcher.data, onCancel, t]);

  return (
    layoutElement &&
    createPortal(
      <Fragment key={'create-price-type'}>
        <ButtonWithIcon onClick={() => setIsOpen(true)} icon={'AddWithoutCircle'} color={'major'}>
          {t('newCta')}
        </ButtonWithIcon>
        {open && (
          <Dialog style={{ minWidth: 600 }} onCancel={onCancel} open={open}>
            <DialogTitle>{t('newModal.title')}</DialogTitle>
            <FormProvider {...methods}>
              <form onSubmit={methods.handleSubmit(onSubmit)}>
                <DialogContent>
                  <span className={'block mb-4'}>{t('form:all_required')}</span>
                  <PriceTypeForm />
                </DialogContent>
                <DialogActions>
                  <ButtonWithIcon onClick={onCancel} icon={'CrossWithoutCircle'} color={'secondary'} type={'button'}>
                    {t('common:cancel')}
                  </ButtonWithIcon>
                  <ButtonWithIcon icon={'CheckWithoutCircle'} type={'submit'}>
                    {t('newModal.submitCta')}
                  </ButtonWithIcon>
                </DialogActions>
              </form>

              <DevTool control={methods.control} />
            </FormProvider>
          </Dialog>
        )}
      </Fragment>,
      layoutElement
    )
  );
}

export default NewPriceType;
